import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Toast,
    ToastHeader,
    ToastBody
} from 'reactstrap';
import Select from 'react-select';
import axios from 'axios';
import { useAuth } from '../../Provider/AuthProvider';
import { Cart, CartItem, ClientInfo, Product } from './Interfaces';

interface CreateProductModalProps {
    isOpen: boolean;
    toggle: () => void;
}

interface Brand {
    id: string;
    marque: string;
}

const CreateProductModal: React.FC<CreateProductModalProps> = ({ isOpen, toggle }) => {
    const { user } = useAuth();
    const [brand, setBrand] = useState('');
    const [contenance, setContenance] = useState('');
    const [prixHT, setPrixHT] = useState('');
    const [cart, setCart] = useState<CartItem[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastColor, setToastColor] = useState('danger');

    useEffect(() => {
        if (isOpen) {
            setLoading(true);
            axios
                .post(
                    'https://api.apapiecesauto.fr/apiV2/',
                    {
                        action: 'constructeurs',
                        entity: user.entity,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.token}`,
                        },
                    }
                )
                .then((response: any) => {
                    if (response.success && response.retour) {
                        setBrands(response.retour);
                        setError(null);
                    } else {
                        setError('Failed to load brands');
                    }
                })
                .catch((err) => {
                    console.error('Error fetching brands:', err);
                    setError('Error fetching brands');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [isOpen, user.entity, user.token]);

    const showToast = (message: string, color: string = 'danger') => {
        setToastMessage(message);
        setToastColor(color);
        setToastOpen(true);
        setTimeout(() => setToastOpen(false), 3000);
    };

    const brandOptions = [
        { value: '', label: 'Sélectionner une marque' },
        ...brands.map((b) => ({
            value: b.marque,
            label: b.marque,
        })),
    ];

    // Function to validate decimal input (numbers with . or ,)
    const validateDecimalInput = (value: string): boolean => {
        const regex = /^(\d+)([.,](\d{0,2})?)?$/;
        return value === '' || regex.test(value);
    };

    const handleContenanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (validateDecimalInput(value)) {
            setContenance(value);
        }
    };

    const handlePrixHTChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (validateDecimalInput(value)) {
            setPrixHT(value);
        }
    };

    // Convert string value to number with proper decimal handling
    const parseDecimalValue = (value: string): number => {
        if (value === '') return 0;
        const cleanedValue = value.replace(',', '.');
        return parseFloat(cleanedValue) || 0;
    };

    const addToCart = (
        label: string,
        client: string,
        quantity: number,
        clientInfo?: ClientInfo
    ) => {
        const savedCart = localStorage.getItem('cart');
        let cart: Cart = savedCart ? JSON.parse(savedCart) : { client: '', items: [], clientInfo: null };

        const newItem: CartItem = {
            cartItemId: `2424220-${Date.now()}`,
            id: "2424220",
            lineId : "",
            articleName: label,
            quantity: 1,
            price_ttc: parseDecimalValue(prixHT),
            price_min: 0,
            immat: "",
            brandName: "",
            ref: ""
        };

        cart = {
            client: client,
            items: [...cart.items, newItem],
            clientInfo: clientInfo || cart.clientInfo ,
            order_id : ""
        };

        localStorage.setItem('cart', JSON.stringify(cart));
        window.dispatchEvent(new Event('cart-updated'));

        return cart;
    };

    const handleAddToCart = () => {
        const quantity = 1;
        const savedCart = localStorage.getItem('cart');
        const cart: Cart = savedCart ? JSON.parse(savedCart) : { client: '', items: [], clientInfo: null };
        const label = "SPECTRO PEINTURE " + contenance + "KG" + " " + brand;

        if (!cart.client) {
            showToast("Client obligatoire !");
            return;
        }

        if (parseDecimalValue(prixHT) <= 0) {
            showToast("Le prix TTC doit être supérieur à 0");
            return;
        }

        if (parseDecimalValue(contenance) <= 0) {
            showToast("La contenance doit être supérieure à 0");
            return;
        }

        const updatedCart = addToCart(label, cart.client, quantity);
        setCart(updatedCart.items);
        showToast("Produit ajouté au panier avec succès!", "success");
        toggle();
        setBrand('');
        setContenance('');
        setPrixHT('');
    };

    // Check if form is valid for enabling the button
    const isFormValid = () => {
        return brand &&
            contenance &&
            parseDecimalValue(contenance) > 0 &&
            prixHT &&
            parseDecimalValue(prixHT) > 0;
    };

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} centered>
                <ModalHeader toggle={toggle}>SPECTRO PEINTURE</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="brand">Marque</Label>
                            {loading ? (
                                <Spinner size="sm" color="primary" />
                            ) : error ? (
                                <div className="text-danger">{error}</div>
                            ) : (
                                <Select
                                    options={brandOptions}
                                    value={brandOptions.find((option) => option.value === brand)}
                                    onChange={(selectedOption: any) => setBrand(selectedOption ? selectedOption.value : '')}
                                    placeholder="Sélectionner une marque"
                                    isSearchable
                                    classNamePrefix="react-select"
                                />
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="contenance">Contenance (KG)</Label>
                            <Input
                                type="text"
                                id="contenance"
                                value={contenance}
                                onChange={handleContenanceChange}
                                inputMode="decimal"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="prixHT">Prix TTC (€)</Label>
                            <Input
                                type="text"
                                id="prixHT"
                                value={prixHT}
                                onChange={handlePrixHTChange}
                                inputMode="decimal"
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>
                        Annuler
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleAddToCart}
                        disabled={!isFormValid() || loading}
                    >
                        <i className="ri ri-shopping-cart-line me-1" />
                        Ajouter au panier
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Toast Notification */}
            <div style={{ position: 'fixed', bottom: '10px', right: '10px', zIndex: 1050 }}>
                <Toast isOpen={toastOpen} className={`bg-${toastColor} text-white`}>
                    <ToastHeader toggle={() => setToastOpen(false)} className={`bg-${toastColor} text-white`}>
                        {toastColor === 'success' ? 'Succès' : 'Erreur'}
                    </ToastHeader>
                    <ToastBody>
                        {toastMessage}
                    </ToastBody>
                </Toast>
            </div>
        </>
    );
};

export default CreateProductModal;